import {
  Card,
  DatePicker,
  Empty,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { TableColumnType, TableProps } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import {
  attendanceApi,
  type AttendanceStatus,
  type StudentMonth,
} from '@/api/attendance';
import { setupApi } from '@/api/setup';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { ExportButton } from '@/shared/components/ui';

const STATUS_LETTER: Record<AttendanceStatus, string> = {
  PRESENT: 'P',
  ABSENT: 'A',
  LATE: 'L',
  HALF_DAY: 'H',
  LEAVE: 'V',
};
const STATUS_COLOR: Record<AttendanceStatus, string> = {
  PRESENT: '#22c55e',
  ABSENT: '#ef4444',
  LATE: '#f59e0b',
  HALF_DAY: '#3b82f6',
  LEAVE: '#9ca3af',
};
const STATUS_LABEL: Record<AttendanceStatus, string> = {
  PRESENT: 'Present',
  ABSENT: 'Absent',
  LATE: 'Late',
  HALF_DAY: 'Half day',
  LEAVE: 'Leave',
};

export function MonthlyAttendancePage() {
  const yearId = useAuthStore((s) => s.academicYearId);
  const [classSectionId, setClassSectionId] = useState<number | undefined>();
  const [month, setMonth] = useState<Dayjs>(dayjs().startOf('month'));

  const { data: classSections } = useQuery({
    queryKey: ['setup', 'class-sections', yearId ?? 'none'],
    queryFn: () => setupApi.listClassSections(yearId ?? undefined),
    enabled: yearId != null,
    staleTime: 5 * 60 * 1000,
  });

  const summaryQuery = useQuery({
    queryKey: ['attendance', 'summary', classSectionId, month.year(), month.month() + 1],
    queryFn: () => attendanceApi.monthlySummary(classSectionId!, month.year(), month.month() + 1),
    enabled: classSectionId != null,
  });

  const summary = summaryQuery.data;
  const days = summary
    ? Array.from({ length: month.daysInMonth() }, (_, i) => i + 1)
    : [];
  const offSet = new Set(summary?.offDates ?? []);

  const dayCols: TableColumnType<StudentMonth>[] = days.map((day) => {
    const date = month.date(day).format('YYYY-MM-DD');
    const isOff = offSet.has(date);
    return {
      key: `d${day}`,
      title: (
        <Tooltip title={month.date(day).format('ddd, D MMM')}>
          <span style={{ opacity: isOff ? 0.4 : 1 }}>{day}</span>
        </Tooltip>
      ),
      width: 28,
      align: 'center',
      render: (_, row) => {
        const m = row.daily.find((d) => d.date === date);
        if (!m) {
          return isOff ? (
            <span style={{ color: '#cbd5e1', fontSize: 11 }}>·</span>
          ) : (
            <span style={{ color: '#cbd5e1' }}>—</span>
          );
        }
        return (
          <Tooltip title={`${STATUS_LABEL[m.status]}${m.halfDayType ? ` (${m.halfDayType.toLowerCase()})` : ''}`}>
            <span
              style={{
                display: 'inline-block',
                minWidth: 18,
                fontSize: 11,
                fontWeight: 600,
                color: STATUS_COLOR[m.status],
              }}
            >
              {STATUS_LETTER[m.status]}
            </span>
          </Tooltip>
        );
      },
    };
  });

  const columns: TableProps<StudentMonth>['columns'] = [
    {
      title: 'Reg #',
      dataIndex: 'registrationNumber',
      width: 110,
      fixed: 'left',
      render: (rn: string) => <Typography.Text code>{rn}</Typography.Text>,
    },
    {
      title: 'Name',
      width: 180,
      fixed: 'left',
      render: (_, row) => (
        <Typography.Text strong>{row.firstName} {row.lastName}</Typography.Text>
      ),
    },
    ...dayCols,
    {
      title: '%',
      width: 80,
      fixed: 'right',
      render: (_, row) => (
        <Tag color={row.attendancePercent >= 75 ? 'success' : row.attendancePercent >= 50 ? 'warning' : 'error'}>
          {row.attendancePercent.toFixed(1)}
        </Tag>
      ),
      sorter: (a, b) => a.attendancePercent - b.attendancePercent,
    },
    {
      title: 'P/A/L/H/V',
      width: 130,
      fixed: 'right',
      render: (_, row) => (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {row.present}/{row.absent}/{row.late}/{row.halfDay}/{row.leave}
        </Typography.Text>
      ),
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Module / Attendance"
        title="Monthly summary"
        description="Per-student daily statuses across the chosen month, with totals and attendance percentage."
        count={summary ? { value: summary.workingDates.length, label: 'working days' } : undefined}
        actions={
          <ExportButton
            items={[{
              kind: 'xlsx',
              url: '/reports/exports/student-attendance-monthly.xlsx',
              params: classSectionId != null
                ? { classSectionId, year: month.year(), month: month.month() + 1 }
                : {},
              fileName: `student-attendance-${month.format('YYYY-MM')}.xlsx`,
              disabled: !summary || classSectionId == null,
            }]}
          />
        }
      />

      <Space wrap size={12}>
        <Select
          placeholder="Class · Section"
          value={classSectionId}
          onChange={setClassSectionId}
          options={(classSections ?? [])
            .slice()
            .sort((a, b) => a.classSortOrder - b.classSortOrder || a.sectionName.localeCompare(b.sectionName))
            .map((cs) => ({
              value: cs.id,
              label: `${cs.className} · ${cs.sectionName}`,
            }))}
          style={{ minWidth: 240 }}
          showSearch
          optionFilterProp="label"
        />
        <DatePicker.MonthPicker
          value={month}
          onChange={(m) => m && setMonth(m.startOf('month'))}
          format="MMM YYYY"
          allowClear={false}
        />
      </Space>

      {classSectionId == null ? (
        <Empty description="Pick a class · section to view the summary" />
      ) : (
        <Card styles={{ body: { padding: 0 } }}>
          <Table
            rowKey="enrollmentId"
            size="small"
            loading={summaryQuery.isFetching}
            columns={columns}
            dataSource={summary?.students ?? []}
            pagination={false}
            scroll={{ x: 'max-content' }}
            footer={() => (
              <Space size="middle" wrap>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>Legend:</Typography.Text>
                {(['PRESENT', 'ABSENT', 'LATE', 'HALF_DAY', 'LEAVE'] as AttendanceStatus[]).map((s) => (
                  <span key={s} style={{ fontSize: 12 }}>
                    <span
                      style={{
                        display: 'inline-block',
                        width: 16,
                        textAlign: 'center',
                        fontWeight: 600,
                        color: STATUS_COLOR[s],
                        marginRight: 4,
                      }}
                    >
                      {STATUS_LETTER[s]}
                    </span>
                    <Typography.Text type="secondary">{STATUS_LABEL[s]}</Typography.Text>
                  </span>
                ))}
              </Space>
            )}
          />
        </Card>
      )}
    </div>
  );
}
